import type { GrenadeEvent, GrenadeTrajectory } from "@/lib/demo-parser";

export function drawGrenadeEffects(
  ctx: CanvasRenderingContext2D,
  grenades: GrenadeEvent[],
  frameTick: number,
  tickRate: number,
  toScreenX: (x: number) => number,
  toScreenY: (y: number) => number
) {
  const recent = grenades.filter((g) => {
    const age = frameTick - g.tick;
    if (age < 0) return false;
    if (g.grenadeType === "smoke") {
      const duration = g.expireTick ? g.expireTick - g.tick : 1152;
      return age <= duration;
    }
    if (g.grenadeType === "molotov") {
      const duration = g.expireTick ? g.expireTick - g.tick : 448;
      return age <= duration;
    }
    return age <= 30;
  });

  recent.forEach((g) => {
    const sx = toScreenX(g.x);
    const sy = toScreenY(g.y);
    const age = frameTick - g.tick;

    if (g.grenadeType === "smoke") {
      const smokeDuration = g.expireTick ? g.expireTick - g.tick : 1152;
      const ageInSeconds = age / tickRate;
      const smokeDurationSeconds = smokeDuration / tickRate;

      if (age < smokeDuration) {
        const expandTime = 2;
        const expansionProgress = Math.min(1, ageInSeconds / expandTime);
        const smokeRadius = 25 + 10 * expansionProgress;

        const fadeStartSeconds = smokeDurationSeconds - 3;
        let opacity = 1;
        if (ageInSeconds < expandTime) {
          opacity = expansionProgress;
        } else if (ageInSeconds > fadeStartSeconds) {
          opacity = Math.max(0, (smokeDurationSeconds - ageInSeconds) / 3);
        }

        // boltobserv-style neutral smoke layers (#9F9D9D outer, #D7D7D7 core)
        ctx.fillStyle = `rgba(159, 157, 157, ${opacity * 0.30})`;
        ctx.beginPath();
        ctx.arc(sx, sy, smokeRadius + 10, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(159, 157, 157, ${opacity * 0.50})`;
        ctx.beginPath();
        ctx.arc(sx, sy, smokeRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(159, 157, 157, ${opacity * 0.65})`;
        ctx.beginPath();
        ctx.arc(sx, sy, smokeRadius - 10, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(215, 215, 215, ${opacity * 0.40})`;
        ctx.beginPath();
        ctx.arc(sx, sy, smokeRadius - 20, 0, Math.PI * 2);
        ctx.fill();

        if (opacity > 0.3) {
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.9})`;
          ctx.font = "bold 11px sans-serif";
          ctx.textAlign = "center";
          ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
          ctx.lineWidth = 3;
          ctx.strokeText("SMOKE", sx, sy - smokeRadius - 5);
          ctx.fillText("SMOKE", sx, sy - smokeRadius - 5);
        }
      }
    } else if (g.grenadeType === "he") {
      const opacity = 1 - age / 30;
      ctx.strokeStyle = `rgba(255, 100, 0, ${opacity})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(sx, sy, age * 2, 0, Math.PI * 2);
      ctx.stroke();
    } else if (g.grenadeType === "flash") {
      const opacity = 1 - age / 30;
      const pulseScale = 1 + Math.sin(age * 0.5) * 0.2;

      // Outer diffuse white
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.9})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 25 * pulseScale, 0, Math.PI * 2);
      ctx.fill();

      // Amber inner ring (boltobserv #FC9F2D)
      ctx.fillStyle = `rgba(252, 159, 45, ${opacity * 0.55})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 18 * pulseScale, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `rgba(255, 255, 200, ${opacity * 0.2})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 50 * pulseScale, 0, Math.PI * 2);
      ctx.fill();

      // Starburst rays
      const rayCount = 8;
      const rayLength = 40 * pulseScale;
      ctx.strokeStyle = `rgba(255, 255, 255, ${opacity * 0.7})`;
      ctx.lineWidth = 3;
      for (let i = 0; i < rayCount; i++) {
        const angle = (i / rayCount) * Math.PI * 2 + age * 0.2;
        const x1 = sx + Math.cos(angle) * 15;
        const y1 = sy + Math.sin(angle) * 15;
        const x2 = sx + Math.cos(angle) * rayLength;
        const y2 = sy + Math.sin(angle) * rayLength;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      if (opacity > 0.3) {
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.font = "bold 11px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("FLASH", sx, sy + 70);
      }
    } else if (g.grenadeType === "molotov") {
      const molotovDuration = g.expireTick ? g.expireTick - g.tick : 448;
      const ageInSeconds = age / tickRate;
      const molotovDurationSeconds = molotovDuration / tickRate;

      if (age < molotovDuration) {
        const spreadTime = 1;
        const spreadProgress = Math.min(1, ageInSeconds / spreadTime);
        const fireRadius = 12 + 18 * spreadProgress;

        const molotovFadeStart = molotovDurationSeconds - 2;
        let opacity = 1;
        if (ageInSeconds > molotovFadeStart) {
          opacity = Math.max(0, (molotovDurationSeconds - ageInSeconds) / 2);
        }

        const flicker1 = Math.sin(ageInSeconds * 10) * 0.1;
        const flicker2 = Math.cos(ageInSeconds * 15) * 0.1;

        // boltobserv inferno: #ff4848 outer, #ce3f00 inner, yellow core
        ctx.fillStyle = `rgba(255, 72, 72, ${opacity * (0.40 + flicker1)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, fireRadius + 10, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(255, 72, 72, ${opacity * (0.60 + flicker2)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, fireRadius, 0, Math.PI * 2);
        ctx.fill();

        const innerRadius = Math.max(1, fireRadius - 10);
        ctx.fillStyle = `rgba(206, 63, 0, ${opacity * (0.70 + flicker1)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, innerRadius, 0, Math.PI * 2);
        ctx.fill();

        const coreRadius = Math.max(1, fireRadius - 20);
        ctx.fillStyle = `rgba(255, 200, 0, ${opacity * (0.80 + flicker2)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, coreRadius, 0, Math.PI * 2);
        ctx.fill();

        // Fire particles
        const particleCount = 12;
        for (let i = 0; i < particleCount; i++) {
          const angle = (i / particleCount) * Math.PI * 2 + ageInSeconds;
          const particleDistance = fireRadius - 5 + Math.sin(ageInSeconds * 5 + i) * 8;
          const px = sx + Math.cos(angle) * particleDistance;
          const py = sy + Math.sin(angle) * particleDistance;
          const particleSize = 3 + Math.random() * 2;

          ctx.fillStyle = `rgba(255, ${100 + Math.random() * 155}, 0, ${opacity * (0.6 + Math.random() * 0.4)})`;
          ctx.beginPath();
          ctx.arc(px, py, particleSize, 0, Math.PI * 2);
          ctx.fill();
        }

        if (opacity > 0.3) {
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          ctx.font = "bold 11px sans-serif";
          ctx.textAlign = "center";
          ctx.strokeStyle = "rgba(0, 0, 0, 0.9)";
          ctx.lineWidth = 3;
          ctx.strokeText("MOLOTOV", sx, sy - fireRadius - 5);
          ctx.fillText("MOLOTOV", sx, sy - fireRadius - 5);
        }
      }
    } else if (g.grenadeType === "decoy") {
      const opacity = 1 - age / 30;
      const pulse = 1 + Math.sin(age * 0.8) * 0.3;

      ctx.fillStyle = `rgba(100, 200, 100, ${opacity * 0.35})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 14 * pulse, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = `rgba(100, 200, 100, ${opacity * 0.85})`;
      ctx.beginPath();
      ctx.arc(sx, sy, 5 * pulse, 0, Math.PI * 2);
      ctx.fill();
    }
  });
}

export function drawGrenadeTrajectories(
  ctx: CanvasRenderingContext2D,
  trajectories: GrenadeTrajectory[],
  frameTick: number,
  roundStartTick: number,
  toScreenX: (x: number) => number,
  toScreenY: (y: number) => number
) {
  const TRAIL_TICKS = 192; // ~3 seconds of visible trail at 64 tick/s
  const COLORS: Record<string, string> = {
    smoke:   "rgba(160,160,160,0.65)",
    he:      "rgba(255,120,0,0.75)",
    flash:   "rgba(255,255,100,0.75)",
    molotov: "rgba(255,60,0,0.75)",
    decoy:   "rgba(100,200,100,0.55)",
  };

  for (const traj of trajectories) {
    if (traj.launchTick < roundStartTick) continue;
    if (traj.launchTick > frameTick) continue;

    const lastPathTick = traj.path.length > 0 ? traj.path[traj.path.length - 1].tick : 0;
    // Hide trail if the grenade landed more than 30 ticks ago (detonation visuals take over)
    if (lastPathTick < frameTick - 30) continue;

    const visible = traj.path.filter(
      (p) => p.tick <= frameTick && p.tick >= frameTick - TRAIL_TICKS
    );
    if (visible.length < 2) continue;

    const color = COLORS[traj.grenadeType] ?? "rgba(200,200,200,0.5)";
    ctx.save();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(toScreenX(visible[0].x), toScreenY(visible[0].y));
    for (const p of visible.slice(1)) {
      ctx.lineTo(toScreenX(p.x), toScreenY(p.y));
    }
    ctx.stroke();
    ctx.setLineDash([]);

    // Small dot at the grenade's current position
    const cur = visible[visible.length - 1];
    ctx.fillStyle = color.replace(/[\d.]+\)$/, "1)");
    ctx.beginPath();
    ctx.arc(toScreenX(cur.x), toScreenY(cur.y), 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
